import { useRef } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { ScrollArea } from "./ui/scroll-area";
import { Upload, Send, MessageSquare } from "lucide-react";

export function ChatWindow({ 
  messages, 
  chatInput, 
  onChatInputChange, 
  onSendMessage, 
  onFileUpload, 
  loading,
  chatRef 
}) {
  const fileRef = useRef(null);
  const isDragging = useRef(false);

  function handleDragOver(e) {
    e.preventDefault();
    isDragging.current = true;
  }

  function handleDragLeave(e) {
    e.preventDefault();
    isDragging.current = false;
  }

  function handleDrop(e) {
    e.preventDefault();
    isDragging.current = false;
    if (e.dataTransfer.files[0]) {
      onFileUpload(e.dataTransfer.files[0]);
    }
  }

  function handleFileChange(e) {
    if (e.target.files[0]) {
      onFileUpload(e.target.files[0]);
    }
  }

  function handleKeyPress(e) {
    if (e.key === "Enter") {
      onSendMessage();
    }
  }

  return (
    <Card className="border-gray-200">
      <CardHeader className="bg-gray-50 border-b border-gray-200 py-3">
        <CardTitle className="flex gap-2 text-[#6A7359] text-base sm:text-lg font-sans">
          <MessageSquare className="w-5 h-5" />
          Quote Concierge
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {/* Messages Area */}
        <ScrollArea className="h-48 sm:h-64 p-3 sm:p-4">
          {messages.map((m, i) => (
            <div 
              key={i} 
              className={"mb-3 flex " + (m.r === "u" ? "justify-end" : "justify-start")}
            >
              <div 
                className={"max-w-[85%] rounded-lg px-3 py-2 " + 
                  (m.r === "u" ? "bg-[#6A7359] text-white" : "bg-gray-100 text-[#1A1A1A]")}
              >
                <p className="text-sm whitespace-pre-wrap">{m.t}</p>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-[#6A7359] rounded-full animate-bounce" />
              <span className="w-2 h-2 bg-[#6A7359] rounded-full animate-bounce" style={{animationDelay:"0.1s"}} />
              <span className="w-2 h-2 bg-[#6A7359] rounded-full animate-bounce" style={{animationDelay:"0.2s"}} />
            </div>
          )}
          <div ref={chatRef} />
        </ScrollArea>

        {/* File Upload Zone */}
        <div 
          className="border-t border-dashed border-gray-200 p-3 sm:p-4 hover:bg-gray-50 transition-colors"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          data-testid="file-upload-zone"
        >
          <div className="text-center">
            <Upload className="w-5 h-5 sm:w-6 sm:h-6 mx-auto text-[#6A7359] mb-1" />
            <p className="text-xs sm:text-sm text-gray-500 mb-2">Drop image/Excel/CSV here</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => fileRef.current.click()} 
              className="border-[#6A7359] text-[#6A7359] text-xs sm:text-sm" 
              data-testid="browse-files-btn"
            >
              Browse
            </Button>
            <input 
              ref={fileRef} 
              type="file" 
              className="hidden" 
              accept=".png,.jpg,.jpeg,.csv,.xlsx,.xls,.pdf" 
              onChange={handleFileChange} 
              data-testid="file-input" 
            />
          </div>
        </div>

        {/* Chat Input */}
        <div className="p-3 sm:p-4 border-t border-gray-200 flex gap-2">
          <Input 
            placeholder="Ask about pricing..." 
            value={chatInput} 
            onChange={(e) => onChatInputChange(e.target.value)} 
            onKeyPress={handleKeyPress} 
            className="border-gray-200 text-sm" 
            data-testid="chat-input" 
          />
          <Button 
            onClick={onSendMessage} 
            disabled={loading || !chatInput.trim()} 
            className="bg-[#6A7359] hover:bg-[#565E48]" 
            data-testid="send-chat-btn"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
